import type { CropListItem } from "@/models/crop";
import CropRepository from "@/repositories/cropRepository";
import { arrayToObject, round } from "@/utils";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { useAuthStore } from "./auth";
import { useGardenStore } from "./garden";

// Active filters for the Cultures page (applied client-side, except the shared
// month range which lives in `monthRange` below).
export interface CropFilters {
  searchText: string;
  selectedUsage: string | null;
  sortBy: string;
}

export const useCropsStore = defineStore("crops", () => {
  const crops = ref<CropListItem[]>([]);
  const areCropsLoaded = ref(false);
  // Crops shown on the Cultures page, filtered server-side (by month range, and
  // by the current date when no range is given — the back never returns them
  // all). Kept separate from `crops`, which the rest of the app relies on in full.
  const filteredCrops = ref<CropListItem[]>([]);
  const areFilteredCropsLoaded = ref(false);
  // Crops for the garden-plan cultivation window (chosen via the plan's month range
  // picker), fetched server-side by month range. Kept separate from `crops` so the
  // plan's window doesn't disturb the rest of the app; folded into `cropsById` below
  // so placed crops from this window still resolve their taxon/icon on the plan.
  const planCrops = ref<CropListItem[]>([]);
  const arePlanCropsLoaded = ref(false);

  // Default cultivation range: this month through +6 months. The date filter is
  // always active (it can be moved but not cleared entirely).
  const toMonthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  const now = new Date();
  const defaultStartMonth = toMonthKey(now);
  const defaultEndMonth = toMonthKey(new Date(now.getFullYear(), now.getMonth() + 6, 1));

  // Single shared cultivation window (["YYYY-MM", "YYYY-MM"]) used by every page
  // that shows crops over a period — Cultures filter, garden plan, and Occupation.
  // Persisted per-viewer in localStorage so the chosen season sticks and is the
  // same everywhere: moving it on one page moves it on all of them.
  const MONTHS_LS_KEY = "cultivation-months";
  const loadMonthRange = (): string[] => {
    try {
      const raw = localStorage.getItem(MONTHS_LS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length === 2 && parsed.every((m) => typeof m === "string")) {
        return [parsed[0], parsed[1]];
      }
    } catch {
      /* ignore malformed/unavailable storage */
    }
    return [defaultStartMonth, defaultEndMonth];
  };
  const monthRange = ref<string[]>(loadMonthRange());
  const startMonth = computed(() => monthRange.value[0] ?? defaultStartMonth);
  const endMonth = computed(() => monthRange.value[monthRange.value.length - 1] ?? defaultEndMonth);
  watch(monthRange, (value) => {
    if (value.length < 2) return; // ignore an in-progress partial selection
    try {
      localStorage.setItem(MONTHS_LS_KEY, JSON.stringify([value[0], value[value.length - 1]]));
    } catch {
      /* ignore unavailable storage */
    }
  });

  const cropFilters = ref<CropFilters>({
    searchText: "",
    selectedUsage: null,
    sortBy: "area_desc",
  });

  // The back defaults an unfiltered fetch to exactly this month → +6 months, so
  // at the default range `crops` (the app-wide list) already IS the filtered list
  // — no separate request needed. Only a custom range warrants `getFilteredCrops`.
  const isDefaultRange = computed(() => startMonth.value === defaultStartMonth && endMonth.value === defaultEndMonth);

  // The list to show before the page's client-side search/usage/sort: at the
  // default range reuse the shared list; for a custom range use the server-
  // filtered list once loaded (falling back so a range change never flashes empty).
  const baseCrops = computed(() => {
    if (isDefaultRange.value) return crops.value;
    return areFilteredCropsLoaded.value ? filteredCrops.value : crops.value;
  });

  // Union of the shared list and the plan window so any placed crop resolves.
  const cropsById = computed<Record<string, CropListItem>>(() =>
    arrayToObject([...crops.value, ...planCrops.value], "id")
  );
  const cropsArea = computed<number>(() =>
    round(
      crops.value.reduce((sum, crop) => sum + crop.area, 0),
      1
    )
  );
  // Crops grouped by taxon id — computed once so consumers (e.g. the
  // self-sufficiency table) don't rescan every crop per row.
  const cropsByTaxon = computed<Record<number, CropListItem[]>>(() => {
    const map: Record<number, CropListItem[]> = {};
    for (const crop of crops.value) {
      (map[crop.taxon] ??= []).push(crop);
    }
    return map;
  });
  // Total planted area (m²) per taxon id, derived from the grouping above.
  const cropsAreaByTaxon = computed<Record<number, number>>(() => {
    const areas: Record<number, number> = {};
    for (const [taxonId, taxonCrops] of Object.entries(cropsByTaxon.value)) {
      areas[Number(taxonId)] = taxonCrops.reduce((sum, crop) => sum + crop.area, 0);
    }
    return areas;
  });
  const cropsCellsCount = computed<number>(() => {
    const garden = useGardenStore().garden;
    if (!garden) return 0;
    const cellSizeMeters = garden.cell_size / 10;
    return crops.value.reduce((count, crop) => count + crop.getCellsCount(cellSizeMeters), 0);
  });

  async function getCrops() {
    const auth = useAuthStore();
    if (!auth.isAuthenticated) return;
    crops.value = await CropRepository.getAll();
    areCropsLoaded.value = true;
  }

  // Load the crops for the garden-plan cultivation window (the shared month range).
  // Passes the garden's resolution (`cell_size`, in decimetres) so each crop carries
  // its plant footprint — the plan needs it to place specimens on an exact footprint.
  async function getPlanCrops() {
    const auth = useAuthStore();
    if (!auth.isAuthenticated) return;
    const cellSize = useGardenStore().garden?.cell_size;
    planCrops.value = await CropRepository.getAll({
      start_month: startMonth.value,
      end_month: endMonth.value,
      ...(cellSize != null ? { cell_size: cellSize } : {}),
    });
    arePlanCropsLoaded.value = true;
  }

  async function getFilteredCrops() {
    const auth = useAuthStore();
    if (!auth.isAuthenticated) return;
    filteredCrops.value = await CropRepository.getAll({ start_month: startMonth.value, end_month: endMonth.value });
    areFilteredCropsLoaded.value = true;
  }

  // NB: `getFilteredCrops` is only needed for a custom range (the default range is
  // already covered by `crops`), and only the Cultures page reads `filteredCrops`.
  // That page (CropHome) drives the fetch while it's mounted, so changing the shared
  // range on the plan/occupation/calendar doesn't trigger a useless crops request.

  // Restore the shared date range to its default (this month → +6 months).
  function resetDateRange() {
    monthRange.value = [defaultStartMonth, defaultEndMonth];
  }

  return {
    crops,
    areCropsLoaded,
    cropFilters,
    monthRange,
    startMonth,
    endMonth,
    isDefaultRange,
    baseCrops,
    cropsById,
    cropsArea,
    cropsByTaxon,
    cropsAreaByTaxon,
    cropsCellsCount,
    planCrops,
    arePlanCropsLoaded,
    getCrops,
    getPlanCrops,
    getFilteredCrops,
    resetDateRange,
  };
});
